import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import JustifiedGrid from './JustifiedGrid';

const InfiniteScrollContainer = forwardRef(({
  children,
  className = '',
  scrollbarColor = '#4A4A4A #1F1E1E',
  scrollbarWidth = 'thin',

  // Data fetching props
  dataFetcher, // Function that returns Promise<{data: [], hasMore: boolean}>
  initialData = [],
  pageSize = 20,
  threshold = 100,

  // Render props
  renderItem, // Function to render each item: (item, index) => JSX
  renderGrid = true, // Whether to use grid layout
  gridCols,

  // Justified layout props
  layout, // 'justified' for Google Photos-style layout, undefined/null for default grid
  targetRowHeight = 200,
  getAspectRatio,
  justifiedGap = 6,

  // Custom components
  loadingComponent = null,
  endMessage = null,
  errorComponent = null,

  // Dependencies that should trigger data reset
  dependencies = [],

  // Deduplication control
  skipDeduplication = false, // Set to true to skip deduplication (e.g., for GIFs)

  ...props
}, ref) => {
  const [items, setItems] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  
  const scrollContainerRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Expose the scroll container ref and data methods to parent
  useImperativeHandle(ref, () => ({
    scrollContainer: scrollContainerRef.current,
    refresh: () => loadData(1, true),
    loadMore: () => loadData(page + 1),
    data: items,
    loading,
    hasMore
  }));

  // Load data function
  const loadData = async (pageNum, reset = false) => {
    if (loading || (!hasMore && !reset) || !dataFetcher) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await dataFetcher(pageNum, pageSize);
      
      if (reset) {
        setItems(result.data || []);
        setPage(1);
      } else {
        if (skipDeduplication) {
          // Skip deduplication - just append new items
          setItems(prev => [...prev, ...(result.data || [])]);
        } else {
          // Deduplicate items based on ID before adding to prevent duplicates
          setItems(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const newItems = (result.data || []).filter(item => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
        setPage(pageNum);
      }
      
      setHasMore(result.hasMore !== false); // Default to true if not specified
    } catch (err) {
      setError(err);
      console.error('InfiniteScrollContainer data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll
  const handleScroll = () => {
    if (!scrollContainerRef.current || loading || !hasMore || !dataFetcher) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadData(page + 1);
    }
  };

  // Load initial data and reset when dependencies change
  useEffect(() => {
    if (dataFetcher) {
      setItems([]);
      setPage(1);
      setHasMore(true);
      setError(null);
      loadData(1, true);
    }
    isInitialLoad.current = false;
  }, [dataFetcher, ...dependencies]);

  // Set up scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && dataFetcher) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [loading, hasMore, dataFetcher, page]);

  // Default components
  const defaultLoadingComponent = (
    <div className="flex justify-center items-center py-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ag-green"></div>
    </div>
  );

  const defaultErrorComponent = error ? (
    <div className="text-center py-4 text-red-400 text-sm">
      Error loading data: {error.message}
      <button 
        onClick={() => loadData(page, true)}
        className="block mx-auto mt-2 px-3 py-1 bg-ag-black-600 rounded hover:bg-ag-black-500"
      >
        Retry
      </button>
    </div>
  ) : null;

  const defaultEndMessage = (
    <div className="text-center py-4 text-gray-400 text-sm">
      No more items to load
    </div>
  );

  // Render content
  const renderContent = () => {
    if (children) {
      return children;
    }

    if (renderItem && items.length > 0) {
      // Justified (Google Photos-style) row layout
      if (layout === 'justified') {
        return (
          <JustifiedGrid
            items={items}
            renderItem={renderItem}
            getAspectRatio={getAspectRatio}
            targetRowHeight={targetRowHeight}
            gap={justifiedGap}
          />
        );
      }

      // Masonry (Pinterest-style) column layout - images keep natural aspect ratio
      if (layout === 'masonry') {
        const gap = justifiedGap || 4;
        return (
          <div style={{ columnCount: gridCols || 2, columnGap: `${gap}px` }}>
            {items.map((item, index) => {
              if (item.isHeader) {
                return (
                  <div key={item.id || `header-${index}`} style={{ columnSpan: 'all', breakInside: 'avoid', marginBottom: `${gap}px` }}>
                    {renderItem(item, index)}
                  </div>
                );
              }
              return (
                <div key={item.id || item._id || index} style={{ breakInside: 'avoid', marginBottom: `${gap}px` }}>
                  {renderItem(item, index)}
                </div>
              );
            })}
          </div>
        );
      }

      const content = items.map((item, index) => renderItem(item, index));

      if (renderGrid) {
        // Map grid columns to Tailwind classes
        const gridColsClass = {
          1: 'grid-cols-1',
          2: 'grid-cols-2',
          3: 'grid-cols-3',
          4: 'grid-cols-4',
          5: 'grid-cols-5',
          6: 'grid-cols-6',
        }[gridCols] || 'grid-cols-2';

        return (
          <div className={`grid ${gridColsClass} gap-2`}>
            {content}
          </div>
        );
      }

      return content;
    }

    return null;
  };

  return (
    <div 
      ref={scrollContainerRef}
      className={`overflow-y-auto ${className}`}
      style={{ 
        scrollbarWidth: scrollbarWidth, 
        scrollbarColor: scrollbarColor 
      }}
      {...props}
    >
      {renderContent()}
      
      {/* Error message */}
      {error && (errorComponent || defaultErrorComponent)}
      
      {/* Loading indicator */}
      {loading && (loadingComponent || defaultLoadingComponent)}
      
      {/* End message */}
      {!hasMore && !loading && !error && items.length > 0 && (endMessage || defaultEndMessage)}
      
      {/* Empty state */}
      {!loading && !error && items.length === 0 && !isInitialLoad.current && (
        <div className="text-center py-8 text-gray-400">
          No items found
        </div>
      )}
    </div>
  );
});

InfiniteScrollContainer.displayName = 'InfiniteScrollContainer';

export default InfiniteScrollContainer;